import clsx from "clsx";
import { mdiLoading } from "@mdi/js";
import Icon from "@mdi/react";

import styles from "./style.module.scss";
import { useAnalytics } from "hooks/useAnalytics";
import analyticsButtonName from "common/analyticsButtonName";

interface IProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  type?: "button" | "submit" | "reset";
  prefixNode?: React.ReactNode;
  suffixNode?: React.ReactNode;
  className?: string;
  variant?: "outline" | "contained" | "text";
  size?: "sm" | "md" | "lg";
  rounded?: boolean;
  iconButton?: boolean;
  loading?: boolean;
}

export default function Button({
  type = "button",
  children,
  prefixNode,
  suffixNode,
  variant,
  size,
  iconButton = false,
  className,
  disabled = false,
  rounded = false,
  loading = false,
  onClick,
  ...restProps
}: Readonly<IProps>) {
  const { trackAllEvents } = useAnalytics();

  const getClassNameByVariant = () => {
    switch (variant) {
      case "text":
        return styles.smartButtonText;

      case "outline":
        return styles.smartButtonOutline;

      case "contained":
        return styles.smartButtonContained;

      default:
        return styles.smartButtonContained;
    }
  };

  const getClassNameBySize = () => {
    switch (size) {
      case "sm":
        return iconButton ? "size-7" : "h-7 px-5";

      case "lg":
        return iconButton ? "size-14" : "h-14 px-5";

      default:
        return iconButton ? "size-11" : "h-11 px-5";
    }
  };

  return (
    <button
      type={type}
      className={clsx(
        styles.smartButton,
        getClassNameByVariant(),
        getClassNameBySize(),
        suffixNode || prefixNode ? "justify-between" : "justify-center",
        rounded ? "rounded-full" : "rounded-base",
        className,
        {
          "!bg-subtitle !ring-subtitle !border-subtitle !text-white cursor-not-allowed":
            disabled || loading
        }
      )}
      disabled={disabled || loading}
      onClick={(event) => {
        trackAllEvents(analyticsButtonName(children));
        onClick && onClick(event);
      }}
      {...restProps}>
      {prefixNode}
      {suffixNode}
      {loading ? <Icon path={mdiLoading} size={1.5} spin={1} /> : children}
    </button>
  );
}
